import { useEffect, useRef, type RefObject } from "react";

/**
 * A click outside an open non-modal popup closes it, and does nothing else.
 *
 * Modal surfaces (Dialog, Select, a default Menu, the lightbox) already get
 * this from Radix, which makes the page behind them ignore the pointer. A
 * non-modal one — a Popover, a row's ⋯ menu, the search results — cannot,
 * because it is non-modal precisely so the list it came from keeps scrolling.
 * Radix closes those on pointerdown but lets the click carry on, so dismissing
 * a menu also ticked whatever checkbox sat under the pointer. Pipe asked for
 * "outside click only closes" on every popup (2026-10-16).
 *
 * So while the popup is open this listens in the capture phase, ahead of every
 * other handler: a pointerdown outside is stopped (no drag, no long-press, no
 * focus move), the popup closes, and the click that follows is swallowed too.
 * Scrolling is untouched — a wheel or a touch-scroll is not a pointerdown on a
 * target that does anything.
 *
 * "Outside" means outside `inside` and outside every Radix popper, so a
 * submenu, a Select inside a Popover, or this popup's own content never count.
 */
export function useCloseOnlyOutside(
  open: boolean,
  close: () => void,
  inside: RefObject<Element | null>,
) {
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (inside.current?.contains(target) || target.closest("[data-radix-popper-content-wrapper]")) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      swallowNextClick();
      closeRef.current();
    }

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, inside]);
}

/**
 * Eats the click that completes the pointerdown just stopped. It is removed a
 * tick after the pointer comes up, so a press that never becomes a click (a
 * drag off the target) cannot leave it armed for a later, unrelated click.
 */
function swallowNextClick() {
  const onClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    disarm();
  };
  const onPointerUp = () => window.setTimeout(disarm, 0);
  function disarm() {
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("pointerup", onPointerUp, true);
    document.removeEventListener("pointercancel", onPointerUp, true);
  }
  document.addEventListener("click", onClick, true);
  document.addEventListener("pointerup", onPointerUp, true);
  document.addEventListener("pointercancel", onPointerUp, true);
}
